import { Button, Card, Form, Image, Input, Modal, Select, Space } from "antd";
import { EditOutlined } from "@ant-design/icons";
import { useState, useEffect } from "react";

import { RobotIconSelector } from "./RobotIconSelector";
import { servicesUrlConfig } from "shared/config/setting";
import { robotsService } from "../services/robotsService";
import { useAuth } from "shared/contexts/AuthProvider";

/**
 * Reusable Robot Form Component
 * @param {Object} robot - Robot data object
 * @param {Array} robotTypes - Available robot types
 * @param {Array} organizations - Available organizations
 * @param {Function} onChange - Handler for form changes (receives partial update object)
 * @param {boolean} showOwner - Whether to show owner field
 * @param {Array} users - Available users (required if showOwner is true)
 * @param {boolean} isEditing - Whether this is edit mode or create mode
 * @param {string} iconMode - 'gallery' or 'upload'
 * @param {Function} onIconModeChange - Handler for icon mode changes
 */
export const RobotForm = ({
  robot,
  robotTypes,
  organizations,
  onChange,
  showOwner = false,
  users = [],
  isEditing = false,
  iconMode = "gallery",
  onIconModeChange,
}) => {
  const { authFetch } = useAuth();
  const [isIconModalVisible, setIsIconModalVisible] = useState(false);
  const [presetIcons, setPresetIcons] = useState([]);
  const [loadingIcons, setLoadingIcons] = useState(false);

  // Load preset icons from API
  useEffect(() => {
    const loadPresetIcons = async () => {
      try {
        setLoadingIcons(true);
        const response = await robotsService.getPresetIcons(authFetch);
        const icons = response.data?.results || response.data || [];

        // Transform API response to icon format
        const formattedIcons = icons.map((icon) => ({
          value: icon.path || icon.id,
          label: icon.name,
          path: icon.path,
        }));

        setPresetIcons(formattedIcons);
      } catch (error) {
        console.error("Error loading preset icons:", error);
        // Fallback to default icon
        setPresetIcons([
          {
            value: "default",
            label: "Default",
            path: "robot_type_icons/unknown_robot_type.png",
          },
        ]);
      } finally {
        setLoadingIcons(false);
      }
    };

    if (authFetch) {
      loadPresetIcons();
    }
  }, [authFetch]);

  const handleIconChange = (iconData) => {
    onChange({
      iconFile: iconData.file,
      icon_path: iconData.path,
      iconType: iconData.type,
    });
  };

  const handleModalOk = () => {
    setIsIconModalVisible(false);
  };

  const handleModalCancel = () => {
    setIsIconModalVisible(false);
  };

  return (
    <Form layout="vertical">
      <Form.Item label="Robot ID">
        <Input
          name="robot_id"
          value={robot.robot_id}
          disabled={isEditing}
          onChange={(e) => onChange({ robot_id: e.target.value })}
        />
      </Form.Item>

      <Form.Item label="Name">
        <Input
          name="name"
          value={robot.name}
          onChange={(e) => onChange({ name: e.target.value })}
        />
      </Form.Item>

      {/* Icon Selector */}
      <Form.Item label="Robot Icon">
        <Space direction="vertical" style={{ width: "100%" }}>
          <Card size="small" title="Current Robot Icon">
            <Space
              direction="vertical"
              align="center"
              style={{ width: "100%" }}
            >
              <Image
                src={servicesUrlConfig.ROBOTS_API.getStaticUrl(
                  robot?.icon_path,
                )}
                alt="Current robot icon"
                style={{
                  width: 120,
                  height: 120,
                  objectFit: "contain",
                  border: "1px solid #d9d9d9",
                  borderRadius: 8,
                  padding: 8,
                }}
                preview={false}
              />
              <Button
                type="primary"
                icon={<EditOutlined />}
                onClick={() => setIsIconModalVisible(true)}
              >
                Change Icon
              </Button>
            </Space>
          </Card>
        </Space>

        {/* Icon Selector Modal */}
        <Modal
          title="Change Robot Icon"
          open={isIconModalVisible}
          onOk={handleModalOk}
          onCancel={handleModalCancel}
          width={700}
        >
          <RobotIconSelector
            currentIconPath={robot.icon_path || null}
            iconFile={robot.iconFile || null}
            onIconChange={handleIconChange}
            mode={iconMode}
            onModeChange={onIconModeChange}
            presetIcons={presetIcons}
            loadingIcons={loadingIcons}
          />
        </Modal>
      </Form.Item>

      <Form.Item label="Organizations">
        <Select
          mode="multiple"
          placeholder="Select robot's organizations"
          value={robot.organizations}
          onChange={(orgs) => onChange({ organizations: orgs })}
          style={{ width: "100%" }}
        >
          {organizations.map((org) => (
            <Select.Option key={org.org_id} value={org.org_id}>
              {org.name}
            </Select.Option>
          ))}
        </Select>
      </Form.Item>

      <Form.Item label="Robot Type">
        <Select
          value={robot.type_id}
          onChange={(typeId) => onChange({ type_id: typeId })}
        >
          {robotTypes.map((type) => (
            <Select.Option key={type.type_id} value={type.type_id}>
              {type.name}
            </Select.Option>
          ))}
        </Select>
      </Form.Item>

      <Form.Item label="Password">
        <Input.Password
          name="password"
          value={robot.password}
          onChange={(e) => onChange({ password: e.target.value })}
          placeholder={isEditing ? "Leave blank to keep current password" : ""}
        />
      </Form.Item>

      {isEditing && (
        <Form.Item label="API Key">
          <Input name="api_key" value={robot.api_key} disabled />
        </Form.Item>
      )}

      {showOwner && (
        <Form.Item label="Owner">
          <Select value={robot.owner} onChange={(owner) => onChange({ owner })}>
            {users.map((user) => (
              <Select.Option key={user.username} value={user.username}>
                {user.username}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
      )}
    </Form>
  );
};
